import { loginSchema } from '@gl/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { FormField } from '@/components/FormField';
import { Spinner } from '@/components/FullScreenLoader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { AuthLayout } from './AuthLayout';

export function LoginPage() {
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next');

  const form = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const login = useMutation({
    mutationFn: api.auth.login,
    onSuccess: (session) => {
      setSession(session);
      navigate(next && next.startsWith('/') ? next : '/', { replace: true });
    },
  });

  const { errors } = form.formState;

  return (
    <AuthLayout
      title="Ingresá a tu cuenta"
      subtitle="Gestioná tu local desde el celu o la compu."
      footer={
        <>
          ¿No tenés cuenta?{' '}
          <Link to="/registro" className="font-medium text-primary hover:underline">
            Registrate
          </Link>
          <span className="mx-2">·</span>
          <Link to="/demo" className="font-medium text-primary hover:underline">
            Probá la demo
          </Link>
        </>
      }
    >
      <form className="grid gap-4" onSubmit={form.handleSubmit((v) => login.mutate(v))} noValidate>
        <FormField label="Email" htmlFor="email" error={errors.email?.message}>
          <Input id="email" type="email" autoComplete="email" inputMode="email" aria-invalid={!!errors.email} {...form.register('email')} />
        </FormField>
        <FormField label="Contraseña" htmlFor="password" error={errors.password?.message}>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            aria-invalid={!!errors.password}
            {...form.register('password')}
          />
        </FormField>
        {login.error && <p className="text-sm text-destructive">{errorMessage(login.error)}</p>}
        <Button type="submit" size="lg" disabled={login.isPending}>
          {login.isPending && <Spinner />} Ingresar
        </Button>
      </form>
    </AuthLayout>
  );
}
